import React, { useEffect, useState, useRef } from 'react';
import { Music, Save, Trash2, Mic } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MondrianButton } from './ui/MondrianButton';
import { MondrianPanel } from './ui/MondrianPanel';
interface Block {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
}
export function RhythmTapper() {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [bpm, setBpm] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const COLORS = ['bg-mondrian-red', 'bg-mondrian-yellow', 'bg-mondrian-blue', 'bg-mondrian-black', 'bg-mondrian-gray'];
  const handleTap = (e?: React.MouseEvent) => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;
    setLastTapTime(now);
    // Calculate BPM roughly
    if (timeDiff > 0) {
      const newBpm = Math.round(60000 / timeDiff);
      setBpm(prev => timeDiff > 2000 ? 0 : Math.round((prev + newBpm) / 2));
    }
    // Determine block properties based on rhythm
    let size = 60; // Default medium
    let color = 'bg-mondrian-yellow';
    if (timeDiff < 200) {
      // Fast tapping -> Small, dense, Broadway style
      size = 30;
      color = Math.random() > 0.5 ? 'bg-mondrian-red' : 'bg-mondrian-blue';
    } else if (timeDiff > 800) {
      // Slow tapping -> Large, calm blocks
      size = 120;
      color = 'bg-mondrian-red';
    } else {
      // Medium
      color = COLORS[Math.floor(Math.random() * COLORS.length)];
    }
    // Position
    // If clicked, use click pos, else random
    let x, y;
    if (e && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      x = e.clientX - rect.left - size / 2;
      y = e.clientY - rect.top - size / 2;
    } else if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      x = Math.random() * (rect.width - size);
      y = Math.random() * (rect.height - size);
    } else {
      return;
    }
    // Snap to grid (20px grid)
    x = Math.round(x / 20) * 20;
    y = Math.round(y / 20) * 20;
    const newBlock: Block = {
      id: now,
      x,
      y,
      size,
      color,
      delay: 0
    };
    setBlocks(prev => [...prev.slice(-50), newBlock]); // Keep last 50 blocks
  };
  // Keyboard listener for spacebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handleTap();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lastTapTime]);
  return <div className="space-y-8" data-id="element-194">
      <div className="text-center space-y-2" data-id="element-195">
        <h2 className="text-2xl font-bold uppercase" data-id="element-196">工具六：爵士律动台</h2>
        <p className="text-gray-600" data-id="element-197">用你的节奏，创作一幅蒙德里安</p>
      </div>

      <div className="bg-yellow-50 border-l-4 border-mondrian-yellow p-4 text-sm text-gray-700 italic" data-id="element-198">
        "蒙德里安热爱爵士乐。他的画作不是死板的数学公式，而是凝固的音乐和舞蹈。现在，点击画布或按空格键打拍子，用你的节奏创作。"
      </div>

      <div className="grid md:grid-cols-4 gap-4" data-id="element-199">
        {/* Canvas */}
        <div className="md:col-span-3" data-id="element-200">
          <MondrianPanel className="h-[500px] relative overflow-hidden cursor-crosshair active:bg-gray-50 transition-colors" borderWidth={8} noPadding data-id="element-201">
            <div ref={containerRef} className="absolute inset-0" onClick={handleTap} data-id="element-202">
              <AnimatePresence data-id="element-203">
                {blocks.map(block => <motion.div key={block.id} initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className={`absolute border-4 border-mondrian-black ${block.color}`} style={{
                left: block.x,
                top: block.y,
                width: block.size,
                height: block.size
              }} data-id="element-204" />)}
              </AnimatePresence>

              {/* Empty State Hint */}
              {blocks.length === 0 && <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30" data-id="element-205">
                  <div className="text-center" data-id="element-206">
                    <Music className="w-24 h-24 mx-auto mb-4" data-id="element-207" />
                    <p className="text-2xl font-bold uppercase" data-id="element-208">
                      Tap to Play Jazz
                    </p>
                  </div>
                </div>}
            </div>
          </MondrianPanel>
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-4" data-id="element-209">
          <MondrianPanel className="flex-1 flex flex-col items-center justify-center text-center space-y-4" data-id="element-210">
            <div className="w-24 h-24 rounded-full border-8 border-mondrian-black flex items-center justify-center bg-white relative overflow-hidden" data-id="element-211">
              <motion.div className="absolute inset-0 bg-mondrian-red opacity-20" animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 60 / (bpm || 60),
              repeat: Infinity
            }} data-id="element-212" />
              <div className="relative z-10 font-black text-2xl" data-id="element-213">
                {bpm}
                <br data-id="element-214" />
                <span className="text-xs font-normal text-gray-500" data-id="element-215">BPM</span>
              </div>
            </div>
            <div className="text-sm font-bold" data-id="element-216">
              {bpm > 120 ? 'Boogie Woogie!' : bpm > 80 ? 'Swing Rhythm' : 'Slow Jazz'}
            </div>
          </MondrianPanel>

          <MondrianButton variant="white" onClick={() => setBlocks([])} fullWidth data-id="element-217">
            <Trash2 className="w-4 h-4 mr-2 inline" data-id="element-218" /> 清空画布
          </MondrianButton>

          <MondrianButton variant="black" onClick={() => alert('作品已保存！')} fullWidth data-id="element-219">
            <Save className="w-4 h-4 mr-2 inline" data-id="element-220" /> 保存作品
          </MondrianButton>
        </div>
      </div>
    </div>;
}